import React from "react"

import { Avatar } from "@coval/ui"

import type { Speaker } from "./messageSpeaker"

interface MessageHeaderProps {
  speaker: Speaker
  className?: string
  agentLabel?: string
  userLabel?: string
  /** The organization's image. Falls back to initials from `agentLabel`. */
  agentAvatarUrl?: string
  /** The persona's face. Falls back to initials from `userLabel`. */
  userAvatarUrl?: string
  agentAvatarTint?: string
  userAvatarTint?: string
  /**
   * Makes the header the message's select control: a button named for the
   * speaker and the message's 1-based position, marked `aria-current` while
   * its message is the selected one. The bubble around it stays
   * plain content so its text reads as text rather than as a button's
   * presentational children.
   */
  onSelect?: (event: React.MouseEvent) => void
  isSelected?: boolean
  messageNumber?: number
}

export const MessageHeader: React.FC<MessageHeaderProps> = ({
  speaker,
  className = "",
  agentLabel = "My Agent",
  userLabel = "Coval",
  agentAvatarUrl,
  userAvatarUrl,
  agentAvatarTint,
  userAvatarTint,
  onSelect,
  isSelected = false,
  messageNumber,
}) => {
  const isPersona = speaker === "persona"
  const displayRole = isPersona ? userLabel : agentLabel
  const avatarUrl = isPersona ? userAvatarUrl : agentAvatarUrl
  const avatarTint = isPersona ? userAvatarTint : agentAvatarTint

  const header = (
    <span
      className={`flex min-w-0 items-center gap-1.5 ${onSelect ? "" : className}`}
    >
      <Avatar
        size="sm"
        name={displayRole}
        src={avatarUrl}
        tint={avatarTint}
        decorative
      />
      <span className="truncate text-xs capitalize">{displayRole}</span>
    </span>
  )

  if (!onSelect) {
    return header
  }

  return (
    <button
      type="button"
      aria-current={isSelected ? "true" : undefined}
      aria-label={
        messageNumber === undefined
          ? displayRole
          : `${displayRole} message ${messageNumber}`
      }
      className={`flex max-w-full rounded-sm text-left ${className}`}
      data-testid="transcript-message-select"
      onClick={onSelect}
    >
      {header}
    </button>
  )
}
